// Setup gsap plugins + config
gsap.registerPlugin(ScrollTrigger);
gsap.config({ nullTargetWarn: false });

// Namespaced utilities
const pfsUtils = {
  breakpoints: {
    md: 992,
  },
  matchMedia: gsap.matchMedia(),
  onDesktopUp: (callback) =>
    pfsUtils.matchMedia.add(
      `(min-width: ${pfsUtils.breakpoints.md}px)`,
      callback
    ),
  onTabletDown: (callback) =>
    pfsUtils.matchMedia.add(
      `(max-width: ${pfsUtils.breakpoints.md - 1}px)`,
      callback
    ),
  clamp: (val, min, max) => (val > max ? max : val < min ? min : val),
  debounce: (callback, delay) => {
    let timer;
    return () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => callback(), delay);
    };
  },
};

// Main class setup
class ProductFeaturesScroller {
  constructor(
    parent,
    items,
    controls,
    intrinsicAssetsWidth = 1920,
    intrinsicAssetsHeight = 1080,
    framesPathPrefix = "https://cdn.shopify.com/s/files/1/0894/7141/8708/files/out",
    framesFileFormat = ".jpg",
    totalFrames = 269
  ) {
    this.parent =
      parent || document.querySelector("[data-product-features-content]");
    this.items =
      items || this.parent.querySelectorAll("[data-product-features-item]");
    this.canvas = this.parent.querySelector("canvas");
    this.canvasContext = this.canvas ? this.canvas.getContext("2d") : null;
    this.intrinsicAssetsWidth = intrinsicAssetsWidth;
    this.intrinsicAssetsHeight = intrinsicAssetsHeight;
    this.framesPathPrefix = framesPathPrefix;
    this.framesFileFormat = framesFileFormat;
    this.totalFrames = totalFrames;
    this.frameStartBuffer = Math.floor(totalFrames * 0.3);
    this.frames = [];
    this.framesObj = { frame: 0 };
    this.framesLoaded = 0;
    this.scrollInertia = 0.35;
    this.activeItemControl = 0;
    this.controls =
      controls ||
      this.parent.querySelectorAll("[data-product-features-control]");
    this.controller = {
      prev: () =>
        (this.activeItemControl =
          this.activeItemControl === 0
            ? this.items.length - 1
            : this.activeItemControl - 1),
      next: () =>
        (this.activeItemControl =
          this.activeItemControl === this.items.length - 1
            ? 0
            : this.activeItemControl + 1),
    };
  }

  // Dynamically size the canvas to fill
  canvasFillViewport() {
    if (!this.canvas) return;

    // Intrinsic dimensions of the asset
    const intHeight = this.intrinsicAssetsHeight;
    const intWidth = this.intrinsicAssetsWidth;
    const intRatio = intHeight / intWidth;

    // Size of the viewport / parent the canvas needs to size to
    const height =
      window.innerWidth < pfsUtils.breakpoints.md
        ? this.parent.offsetHeight
        : window.innerHeight;
    const width = window.innerWidth;
    const ratio = height / width;

    if (ratio > intRatio) {
      this.canvas.style.width = `${height / intRatio}px`;
      this.canvas.style.height = `${height}px`;
      this.canvas.style.marginLeft = `${-height / intRatio / 2}px`;
      this.canvas.style.left = "50%";
      this.canvas.style.top = "0";
      this.canvas.style.marginTop = "0";
    } else {
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${width * intRatio}px`;
      this.canvas.style.marginLeft = "0";
      this.canvas.style.left = "0";
      this.canvas.style.top = "50%";
      this.canvas.style.marginTop = (-width * intRatio) / 2 + "px";
    }
  }

  canvasSetup() {
    if (!this.canvas) return;

    this.canvas.width = this.intrinsicAssetsWidth;
    this.canvas.height = this.intrinsicAssetsHeight;

    this.canvasFillViewport();

    const resizeHandler = pfsUtils.debounce(
      () => this.canvasFillViewport(),
      300
    );
    window.addEventListener("resize", resizeHandler);
  }

  currentFrame(index) {
    // Command to convert the mp4 file to a sequence of jpg frames:
    // $ ffmpeg -i <video.mp4> -q:v 1 -vf fps=36 file/out%d.jpg
    return `${this.framesPathPrefix}${(index + 1).toString()}${
      this.framesFileFormat
    }`;
  }

  render() {
    if (!this.canvas || !this.canvasContext) return;

    this.canvasContext.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.canvasContext.drawImage(this.frames[this.framesObj.frame], 0, 0);
  }

  loadFrames() {
    gsap.set(this.canvas, { opacity: 0 });

    const promises = new Array(this.totalFrames)
      .fill("")
      .map((_, frameIndex) => {
        return new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = () =>
            reject(`Frame failed to load frame ${frameIndex}`);
          image.src = this.currentFrame(frameIndex);
          image.addEventListener("load", (e) => {
            this.framesLoaded++;

            if (this.framesLoaded === this.totalFrames) {
              this.parent.classList.add("is-loaded");
              gsap.to(this.canvas, {
                opacity: 1,
                duration: 0.5,
                ease: "power2.inOut",
              });
              this.render();
              return false;
            }
          });
        });
      });

    return Promise.all(promises);
  }

  moveToFrame(frame, options) {
    gsap.to(this.framesObj, {
      frame: frame,
      snap: "frame",
      ease: "none",
      onUpdate: () => this.render(),
      ...options,
    });
  }

  scrub() {
    this.moveToFrame(this.totalFrames - 1, {
      scrollTrigger: {
        trigger: this.canvas.parentElement,
        start: "top top",
        end: () => {
          return this.items.length
            ? `+=${
                this.items[this.items.length - 1].offsetTop -
                this.items[this.items.length - 1].offsetHeight /
                  (this.items.length * this.items.length)
              }`
            : `+=${this.totalFrames + 25}%`; // Add a little extra buffer for the items animation to finish
        },
        scrub: this.scrollInertia,
        pin: this.canvas.parentElement,
        invalidateOnRefresh: true,
      },
    });
  }

  introOutroAnims() {
    const animValues = {
      clip: "clamp(3rem, 15.92vw + -4.64rem, 9.69rem)",
      radius: "12px",
      scale: 1.12,
    };

    const parentAnimValues = {
      clipPath: () => {
        return `inset(
          ${animValues.clip}
          ${animValues.clip}
          ${animValues.clip}
          ${animValues.clip}
          round ${animValues.radius}
        )`;
      },
    };

    const canvasAnimValues = {
      scale: () => {
        return animValues.scale;
      },
    };

    const intro = gsap
      .timeline({
        scrollTrigger: {
          trigger: this.canvas.parentElement,
          start: "top center",
          end: "top top",
          scrub: this.scrollInertia,
        },
      })
      .addLabel("start", 0);
    intro.from(this.canvas.parentElement, { ...parentAnimValues }, "start");
    intro.from(this.canvas, { ...canvasAnimValues }, "start");

    const outro = gsap
      .timeline({
        scrollTrigger: {
          trigger: this.parent,
          start: "bottom 75%",
          end: () => `+=${window.innerHeight / 1.5}px`,
          scrub: this.scrollInertia,
        },
      })
      .addLabel("start", 0);
    outro.to(this.canvas.parentElement, { ...parentAnimValues }, "start");
    outro.to(this.canvas, { ...canvasAnimValues }, "start");
  }

  itemsAnimDesktopUp() {
    if (!this.items.length) return;

    this.items.forEach((item) => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: item.querySelector("*:first-child"),
          start: "40% bottom",
          end: "+=80%",
          scrub: this.scrollInertia,
        },
      });

      timeline
        .from(item, { opacity: 0, duration: 0.2 })
        .to(item, { opacity: 0, duration: 0.2, delay: 0.4 });
    });
  }

  itemsAnimTabletDown(animateFrames = true) {
    if (!this.items.length) return;

    const timeline = gsap.timeline({
      onComplete: () => this.items[this.activeItemControl].focus(),
    });

    timeline.to(this.items, {
      autoAlpha: 0,
      duration: 0.4,
      ease: "power2.out",
    });
    timeline.set(this.items, { xPercent: this.activeItemControl * -100 });
    timeline.to(
      this.items[this.activeItemControl],
      { autoAlpha: 1, duration: 0.4, ease: "power2.in" },
      "-=0.2"
    );

    if (animateFrames && this.framesLoaded === this.totalFrames) {
      // Needed to calculate the progress of the frames alongside the active item
      const reverseIndex = this.items.length - this.activeItemControl;

      // Force an offset
      // If back to start, force it to the smallest frame amount
      // Otherwise, nudge it forward by 21 frames to match the desktop positioning of the frames + text
      const frameOffset =
        this.activeItemControl === 0
          ? -this.totalFrames
          : Math.floor(this.totalFrames * 0.125);

      this.moveToFrame(
        pfsUtils.clamp(
          Math.floor(this.totalFrames / reverseIndex) + frameOffset,
          this.frameStartBuffer,
          this.totalFrames - 1
        ),
        { duration: 1.25 }
      );
    }
  }

  // Initiate all animations
  animSetup() {
    pfsUtils.onDesktopUp(() => {
      this.scrub();
      this.introOutroAnims();
      this.itemsAnimDesktopUp();
    });

    pfsUtils.onTabletDown(() => {
      this.activeItemControl = 0;
      this.moveToFrame(this.frameStartBuffer);
      this.itemsAnimTabletDown(false);
    });

    ScrollTrigger.refresh();
  }

  // Button controls
  handleControls(direction = null) {
    if (null !== direction) {
      this.controller[direction]();
      this.itemsAnimTabletDown();
    }

    // Disable prev / next if at beginning / end
    if (this.controls.length) {
      Array.from(this.controls).filter((control) => {
        const directionAttr = control.dataset.productFeaturesControl;

        if ("prev" === directionAttr) {
          control.disabled = this.activeItemControl === 0;
        } else if ("next" === directionAttr) {
          control.disabled = this.activeItemControl === this.items.length - 1;
        }
      });
    }
  }

  controlsSetup() {
    if (!this.controls.length) return;

    this.handleControls();

    this.controls.forEach((control) => {
      control.addEventListener("click", () =>
        this.handleControls(control.dataset.productFeaturesControl)
      );
    });
  }

  async init() {
    this.canvasSetup();
    this.frames = await this.loadFrames();
    this.animSetup();
    this.controlsSetup();
  }
}

// Initiate the scroller
document.addEventListener("DOMContentLoaded", () => {
  const scroller = new ProductFeaturesScroller();
  scroller.init();
});
